import {
  Bookmark,
  Heart,
  PackageCheckIcon,
  Plus,
  Share2,
  Boxes,
} from "lucide-react";
import { Button } from "../button";
import { Stock } from "@/app/productos/Stock";
import { Product } from "@/lib/models/product";
import { useApi, GenericApiResponse } from "@/lib/hooks/useApi";
import { useModal } from "@/lib/hooks/useModal";
import SharedForm from "./SharedForm";
import { CustomTooltip } from "@/components/ui/tooltip";
import { FavoriteListForm } from "@/app/usuario/favoritos/FavoriteListForm";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Modal } from "@/components/ui/Modal";
import { ProductRecomended } from "@/app/producto/[id]/ProductRecomended";
import { useState, useEffect, useRef, useCallback } from "react";
import { canViewStock as checkCanViewStock } from "@/lib/utils/pricingVisibility";

interface Wishlist {
  id: string;
  name: string;
  itemCount?: number;
  [key: string]: unknown;
}

export function WishlistSelection({
  productId,
  onClose,
}: {
  productId: string;
  onClose: () => void;
}) {
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const { apiRequest } = useApi("");
  const { showModal, closeTopModal } = useModal();

  const getList = useCallback(() => {
    apiRequest<GenericApiResponse<Wishlist[]>>("", {
      method: "get",
      url: `${process.env.NEXT_PUBLIC_API_URL}/purchasing/wishlists`,
    }).then((res) => {
      setWishlists(Array.isArray(res?.data) ? res.data : []);
    });
  }, [apiRequest]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleSelect = (wishlistId: string) => {
    apiRequest("", {
      method: "post",
      url: `${process.env.NEXT_PUBLIC_API_URL}/purchasing/wishlists/${wishlistId}/items`,
      data: {
        productId,
      },
      showToast: true,
      loadingMessage: "Agregando a la lista...",
    }).then(() => {
      onClose();
    });
  };

  function handleSendList(name: string) {
    apiRequest("", {
      method: "post",
      url: `${process.env.NEXT_PUBLIC_API_URL}/purchasing/wishlists`,
      data: {
        name,
      },
      showToast: true,
      loadingMessage: "Creando lista...",
    }).then(() => {
      closeTopModal();
      getList();
    });
  }

  function handleCreateList() {
    showModal({
      content: (
        <FavoriteListForm onSubmit={handleSendList} submitLabel="Crear lista" />
      ),
      title: "Crear lista",
    });
  }

  return (
    <div className="flex flex-col gap-3 py-2 max-h-[60vh] overflow-y-auto scrollbar-thin px-1">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={handleCreateList}
          tooltip="Crear nueva lista de favoritos"
        >
          <Plus size={16} />
        </Button>
      </div>
      {wishlists.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <Bookmark className="w-8 h-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 font-medium">
            No tienes listas de favoritos creadas.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {wishlists.map((list) => (
            <button
              key={list.id}
              onClick={() => handleSelect(list.id)}
              className="flex items-center p-3 gap-3 rounded-xl border border-gray-200 bg-white hover:border-primary hover:shadow-sm hover:bg-primary/5 transition-all duration-200 group text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50/50 flex items-center justify-center text-blue-500 group-hover:bg-blue-100 transition-colors shrink-0">
                <Bookmark
                  size={18}
                  className="fill-current opacity-20 group-hover:opacity-100 transition-opacity"
                />
              </div>
              <div className="flex-1 min-w-0">
                <CustomTooltip content={list.name} side="top">
                  <h3 className="text-sm font-semibold text-gray-800 truncate group-hover:text-primary transition-colors">
                    {list.name}
                  </h3>
                </CustomTooltip>
                <p className="text-xs text-gray-500 mt-0.5">
                  {list.itemCount ?? 0}{" "}
                  productos
                </p>
              </div>
              <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-primary text-white transition-all transform translate-x-2 group-hover:translate-x-0 shrink-0">
                <Plus size={14} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface RelatedProductSumbenuProps {
  isOpen: boolean;
  setShowSubmenu: (show: boolean) => void;
  product: Product;
  recomended?: boolean;
  onClose: () => void;
  triggerClassName?: string;
}

export function RelatedProductSumbenu({
  isOpen,
  setShowSubmenu,
  product,
}: RelatedProductSumbenuProps) {
  const { showModal, closeTopModal } = useModal();
  const { data: session } = useSession();
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowSubmenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setShowSubmenu]);

  function getIds(): string[] {
    return (product.relatedProducts ?? []).map((item) => item.id);
  }

  const [showModalPoduc, setshowModalPoduc] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const isFavorite = false;

  const canViewStock = checkCanViewStock(session, product);
  const relatedIds = getIds();

  return (
    <div className="relative" ref={menuRef}>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-1 w-48 sm:w-56 z-50 overflow-hidden">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!session?.user) {
                router.push("/iniciar-sesion");
                setShowSubmenu(false);
                return;
              }
              showModal({
                title: "Seleccionar lista de favoritos",
                size: "md",
                content: (
                  <WishlistSelection
                    productId={product.id}
                    onClose={closeTopModal}
                  />
                ),
              });
              setShowSubmenu(false);
            }}
            className="group/item w-full px-4 py-3 text-left hover:bg-primary text-gray-700 hover:text-white flex items-center gap-3 text-sm transition-colors cursor-pointer"
          >
            <Heart className="w-5 h-5 text-red-500 group-hover/item:text-white transition-colors shrink-0" />
            <span className="truncate">
              {isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
            </span>
          </button>
          {relatedIds.length !== 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setshowModalPoduc(true);
                setShowSubmenu(false);
              }}
              disabled={relatedIds.length === 0}
              className={`${
                relatedIds.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:bg-primary hover:text-white"
              } group/item w-full px-4 py-3 text-left text-gray-700 flex items-center gap-3 text-sm transition-colors`}
            >
              <Boxes className="w-5 h-5 text-indigo-500 group-hover/item:text-white transition-colors shrink-0" />
              <span className="truncate">Ver relacionados</span>
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              showModal({
                title: "Compartir producto",
                content: <SharedForm product={product} />,
                size: "sm",
              });
              setShowSubmenu(false);
            }}
            className="group/item w-full px-4 py-3 text-left hover:bg-primary text-gray-700 hover:text-white flex items-center gap-3 text-sm transition-colors cursor-pointer"
          >
            <Share2 className="w-5 h-5 text-blue-500 group-hover/item:text-white transition-colors shrink-0" />
            <span className="truncate">Compartir</span>
          </button>
          {canViewStock && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsStockModalOpen(true);
                setShowSubmenu(false);
              }}
              className="group/item w-full px-4 py-3 text-left hover:bg-primary text-gray-700 hover:text-white flex items-center gap-3 text-sm transition-colors cursor-pointer"
            >
              <PackageCheckIcon className="w-5 h-5 text-emerald-500 group-hover/item:text-white transition-colors shrink-0" />
              <span className="truncate">Ver stock</span>
            </button>
          )}
        </div>
      )}
      <Button
        variant={"ghostPrimary"}
        aria-label={`Ver opciones del producto ${product?.sku || ""}`}
        aria-expanded={isOpen}
        tooltip="Ver más"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowSubmenu(!isOpen);
        }}
      >
        <Plus className="size-4.5 w-5 h-5 transition-transform" />
      </Button>
      <Modal
        isOpen={showModalPoduc}
        onClose={() => setshowModalPoduc(false)}
        title={`Productos relacionados ${product?.sku || ""}`}
        fullscreen
      >
        {relatedIds.length > 0 && (
          <ProductRecomended
            productsIds={relatedIds}
            modal={true}
          />
        )}
      </Modal>
      <Modal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        title={`Disponibilidad: ${product.sku}`}
        size="md"
        closeButton={false}
      >
        <Stock product={product} />
      </Modal>
    </div>
  );
}
